import type { ICampaignGroup } from '~/modules/campaign-group/types';

import { useCallback, useEffect, useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import { BlockStack, InlineGrid, InlineStack, Text } from '@shopify/polaris';
import { CAMPAIGN_BACKGROUND_MAIN } from '~/constants/campaign';
import { GVariantBadge, GRangeSlider } from '~/presentation/ui';

export type TrafficRateRef = {
  getValues: () => {
    trafficRate: ICampaignGroup['trafficRate'];
  };
};

interface SettingTrafficRateProps {
  campaignData: ICampaignGroup;
  isDisabled: boolean;
}

export const SettingTrafficRate = forwardRef<TrafficRateRef, SettingTrafficRateProps>(
  (props, ref) => {
    const { isDisabled, campaignData } = props;
    const variantData = useMemo(() => {
      const rangeValue = campaignData?.trafficRate ?? 0.5;
      const originTraffic = rangeValue;
      const variantTraffic = 1 - rangeValue;
      return [
        {
          id: '1',
          isOrigin: true,
          trafficRate: originTraffic,
        },
        {
          id: '2',
          isOrigin: false,
          trafficRate: variantTraffic,
        },
      ];
    }, [campaignData]);

    const getValueTraffic = useCallback(() => {
      const variant = variantData?.find((v) => !!v?.isOrigin);
      return variant?.trafficRate ? Math.round(variant.trafficRate * 100) : 0;
    }, [variantData]);

    const [rangeValue, setRangeValue] = useState(getValueTraffic());

    useEffect(() => {
      setRangeValue(getValueTraffic());
    }, [getValueTraffic]);

    const handleRangeSliderChange = useCallback((value: number) => {
      setRangeValue(value);
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        getValues: () => {
          const originTraffic = rangeValue / 100;
          return { trafficRate: originTraffic };
        },
      }),
      [rangeValue],
    );

    return (
      <BlockStack gap="300">
        <Text as="h2" variant="headingMd">
          Set traffic for each variant
        </Text>
        <BlockStack>
          <GRangeSlider
            id="traffic-rate-slider"
            output
            label=""
            disabled={isDisabled}
            min={0}
            max={100}
            value={rangeValue}
            backgroundLower={CAMPAIGN_BACKGROUND_MAIN.ORIGIN}
            backgroundUpper={CAMPAIGN_BACKGROUND_MAIN.VARIANT}
            onChange={handleRangeSliderChange}
          />
          <InlineGrid columns="1fr auto">
            <InlineStack gap="200" blockAlign="center">
              <GVariantBadge
                hideVariant
                label="Control"
                variant="A"
                tone="active"
                labelProps={{
                  variant: 'bodyMd',
                }}
              />
              {'-'}
              <Text as="p" fontWeight="semibold" variant="headingMd">
                {rangeValue}%
              </Text>
            </InlineStack>
            <InlineStack gap="200" blockAlign="center">
              <GVariantBadge
                hideVariant
                label="Variant"
                variant="B"
                labelProps={{
                  variant: 'bodyMd',
                }}
              />
              {'-'}
              <Text as="p" fontWeight="semibold" variant="headingMd" numeric>
                {100 - rangeValue}%
              </Text>
            </InlineStack>
          </InlineGrid>
        </BlockStack>
      </BlockStack>
    );
  },
);

SettingTrafficRate.displayName = 'SettingTrafficRate';
